// ============================================================
// AD Scorecard
// One page per Account Director: monthly client counts, turnover,
// and the Supervising AD's 1:1 meeting notes.
// Visible only to the profile ids in adScorecardConfig.js.
// ============================================================
import { useState, useEffect, useMemo, useCallback } from 'react'
import { RefreshCw, Lock, Unlock, Plus, Trash2, TrendingDown, TrendingUp, Users,
  Eye, Check, X, Clock3, ArrowRight } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import {
  monthLabel, currentMonthKey, effectiveTerminating,
  turnoverRate, rollingTurnover, netGrowth, formatPct, turnoverTone, annualTone,
  resolveDirectors, AD_SCORECARD_EXCLUDED,
  COMPANY_ID, buildCompanyRows, annualize,
  TARGET_MONTHLY, STRETCH_MONTHLY, targetProgress,
  TARGET_ROLLING, STRETCH_ROLLING, TARGET_ANNUAL, STRETCH_ANNUAL, annualTargetProgress,
  sumReasons, splitChurn, REASON_LABELS,
  WATCH_REASONS, getWatchReason, WATCH_SEVERITY, WATCH_STATUS,
  canViewAllScorecards, visibleDirectorsFor,
  TENURE_BUCKETS, churnSpeed, churnSpeedFromRows, tenureMonths,
} from './adScorecardConfig'

// ── Small presentational bits ───────────────────────────────────────────────

function StatCard({ label, value, sub, tone }) {
  return (
    <div style={{
      background: tone?.bg && tone.bg !== 'transparent' ? tone.bg : '#fff',
      border: '1px solid var(--border-light)', borderRadius: 10,
      padding: '14px 16px', flex: 1, minWidth: 130,
    }}>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, color: tone?.color || 'var(--navy)', lineHeight: 1.3 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{sub}</div>}
    </div>
  )
}

// Editable terminating-clients cell. Shows the computed number until someone
// overrides it; the override is what feeds turnover from then on.
function TerminatingCell({ row, onSave, disabled }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  const auto = row.terminating_auto ?? 0
  const overridden = row.terminating_override !== null && row.terminating_override !== undefined
  const value = effectiveTerminating(row)

  const start = () => {
    if (disabled) return
    setDraft(String(value))
    setEditing(true)
  }

  const commit = async () => {
    setEditing(false)
    const trimmed = draft.trim()
    // Empty input clears the override and falls back to the computed number.
    const next = trimmed === '' ? null : Number(trimmed)
    if (next !== null && (Number.isNaN(next) || next < 0)) return
    if (next === row.terminating_override) return
    await onSave(row, next)
  }

  if (editing) {
    return (
      <input
        className="form-control"
        style={{ width: 70, fontSize: 12.5, padding: '4px 6px' }}
        value={draft}
        autoFocus
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') setEditing(false)
        }}
      />
    )
  }

  return (
    <span
      onClick={start}
      title={disabled ? 'Month is locked' : overridden ? `Overridden — computed was ${auto}. Click to edit, clear to reset.` : 'Click to override'}
      style={{
        cursor: disabled ? 'default' : 'pointer',
        borderBottom: disabled ? 'none' : '1px dashed var(--border)',
        color: overridden ? 'var(--mauve)' : 'var(--text-primary)',
        fontWeight: overridden ? 600 : 400,
      }}>
      {value}{overridden ? ' *' : ''}
    </span>
  )
}

// Target strip: where they are against the 5% goal, in clients rather than
// percentages. "Up to 2 a month" is actionable in a 1:1; "5%" isn't.
function TargetBar({ base, actual, rollingPct, annualPct, avgBase }) {
  const goal = targetProgress(base, actual, TARGET_MONTHLY)
  const stretch = targetProgress(base, actual, STRETCH_MONTHLY)

  const Card = ({ label, sub, met, headline, detail }) => (
    <div style={{ flex: 1, minWidth: 190, padding: '10px 14px', borderRadius: 10,
      background: met ? 'var(--lightgreen-bg)' : '#fff8e1',
      border: `1px solid ${met ? 'var(--green)' : '#f0d98a'}` }}>
      <div style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted)' }}>
        {label} · {sub}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: met ? 'var(--green)' : '#8a6000', marginTop: 2 }}>
        {headline}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{detail}</div>
    </div>
  )

  // Monthly: the number of clients that can be lost this month.
  const monthRow = goal && (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
      <Card label="Goal" sub={`${TARGET_MONTHLY}%/mo`} met={goal.met}
        headline={goal.met ? `On target — ${goal.actual} of ${goal.allowed} allowed`
                           : `${goal.gap} over — save ${goal.gap} more client${goal.gap === 1 ? '' : 's'}`}
        detail={`At ${base} clients, keep losses to ${goal.allowed}/mo or fewer`} />
      <Card label="Stretch" sub={`${STRETCH_MONTHLY}%/mo`} met={stretch.met}
        headline={stretch.met ? `On target — ${stretch.actual} of ${stretch.allowed} allowed`
                              : `${stretch.gap} over — save ${stretch.gap} more client${stretch.gap === 1 ? '' : 's'}`}
        detail={`At ${base} clients, keep losses to ${stretch.allowed}/mo or fewer`} />
    </div>
  )

  // Annual: same idea, but clients per year over the trailing window.
  const roll = annualTargetProgress(avgBase, rollingPct, TARGET_ROLLING)
  const rollStretch = annualTargetProgress(avgBase, rollingPct, STRETCH_ROLLING)
  const ann = annualTargetProgress(avgBase, annualPct, TARGET_ANNUAL)

  const annualRow = (roll || ann) && (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
      {roll && (
        <Card label="Rolling 12mo" sub={`target under ${TARGET_ROLLING}%`} met={roll.met}
          headline={roll.met
            ? `On target — ${formatPct(rollingPct)} vs ${TARGET_ROLLING}%`
            : `${formatPct(rollingPct)} — ${roll.gap} too many lost`}
          detail={rollStretch?.met
            ? `Also beating the ${STRETCH_ROLLING}% stretch`
            : `Keep to ${roll.allowedPerYear}/yr for goal, ${rollStretch?.allowedPerYear ?? 0}/yr for stretch`} />
      )}
      {ann && (
        <Card label="Annualized" sub={`target under ${TARGET_ANNUAL.toFixed(0)}%`} met={ann.met}
          headline={ann.met
            ? `On target — ${formatPct(annualPct, 0)} vs ${TARGET_ANNUAL.toFixed(0)}%`
            : `${formatPct(annualPct, 0)} of the book lost per year`}
          detail={`Goal is under ${TARGET_ANNUAL.toFixed(0)}%, stretch under ${STRETCH_ANNUAL.toFixed(0)}%`} />
      )}
    </div>
  )

  if (!monthRow && !annualRow) return null
  return <>{monthRow}{annualRow}</>
}

// Why clients left, over the last 12 months. The point of this panel is the
// controllable/total split — an AD can move the first number, not the second.
function ChurnByCause({ rows }) {
  const tally = sumReasons(rows.slice(-12))
  const split = splitChurn(tally)
  if (!split.total) return null

  const entries = Object.entries(tally).sort((a, b) => b[1] - a[1])
  const pct = n => Math.round((n / split.total) * 100)

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-header">
        <span className="card-title">Why they left · last 12 months</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{split.total} clients</span>
      </div>
      <div className="card-body">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <div style={{ flex: 1, minWidth: 150, padding: '10px 14px', borderRadius: 10,
            background: 'var(--red-bg)', border: '1px solid #f0c4c4' }}>
            <div style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted)' }}>Controllable</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--red)' }}>{split.controllable}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{pct(split.controllable)}% — coach this</div>
          </div>
          <div style={{ flex: 1, minWidth: 150, padding: '10px 14px', borderRadius: 10,
            background: '#fff8e1', border: '1px solid #f0d98a' }}>
            <div style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted)' }}>Structural</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: '#8a6000' }}>{split.structural}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{pct(split.structural)}% — pricing / qualifying</div>
          </div>
          <div style={{ flex: 1, minWidth: 150, padding: '10px 14px', borderRadius: 10,
            background: 'var(--cream)', border: '1px solid var(--border-light)' }}>
            <div style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted)' }}>Not real churn</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--text-muted)' }}>{split.notChurn}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{pct(split.notChurn)}% — project work</div>
          </div>
        </div>

        {entries.map(([k, n]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
            <div style={{ width: 175, fontSize: 12.5, color: 'var(--text-secondary)', flexShrink: 0 }}>
              {REASON_LABELS[k] || k}
            </div>
            <div style={{ flex: 1, height: 16, background: 'var(--cream-dark)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${pct(n)}%`, height: '100%',
                background: ['value_not_shown','results','expectations','relationship'].includes(k)
                  ? 'var(--red)' : k === 'cant_afford' ? '#c4962a' : 'var(--text-muted)' }} />
            </div>
            <div style={{ width: 48, fontSize: 12.5, textAlign: 'right', color: 'var(--text-muted)' }}>{n} · {pct(n)}%</div>
          </div>
        ))}

        {split.unknown > 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 10, fontStyle: 'italic' }}>
            {split.unknown} with no reason recorded — the split above is that much less reliable.
          </div>
        )}
      </div>
    </div>
  )
}

// How fast clients leave — the other half of the churn picture. Same rate can
// mean opposite problems depending on when in the relationship it happens.
function ChurnSpeed({ rows }) {
  // Same rows the reasons chart reads, so the two totals always agree.
  const sp = churnSpeedFromRows(rows.slice(-12))
  if (!sp) return null

  const worst = TENURE_BUCKETS
    .map(b => ({ ...b, n: sp.buckets[b.key] || 0 }))
    .sort((a, b) => b.n - a.n)[0]

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-header">
        <span className="card-title"><Clock3 size={13} /> How fast they leave</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{sp.total} terminations</span>
      </div>
      <div className="card-body">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          {[
            { label: 'Median tenure', v: `${sp.median.toFixed(1)} mo`, c: 'var(--navy)',
              sub: 'half leave before this' },
            { label: 'Gone by 3 months', v: `${Math.round(sp.pct90)}%`,
              c: sp.pct90 >= 25 ? 'var(--red)' : sp.pct90 >= 15 ? '#c4962a' : 'var(--green)',
              sub: `${sp.within90} client${sp.within90 === 1 ? '' : 's'}` },
            { label: 'Gone by 6 months', v: `${Math.round((sp.within180 / sp.total) * 100)}%`,
              c: 'var(--text-secondary)', sub: `${sp.within180} clients` },
          ].map(x => (
            <div key={x.label} style={{ flex: 1, minWidth: 140, padding: '11px 13px',
              borderRadius: 10, border: '1px solid var(--border-light)' }}>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase',
                letterSpacing: 0.4 }}>{x.label}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: x.c }}>{x.v}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{x.sub}</div>
            </div>
          ))}
        </div>

        {TENURE_BUCKETS.map(b => {
          const n = sp.buckets[b.key] || 0
          const pct = Math.round((n / sp.total) * 100)
          return (
            <div key={b.key} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 62, fontSize: 12.5, color: 'var(--text-secondary)', flexShrink: 0 }}>
                  {b.label}
                </div>
                <div style={{ flex: 1, height: 16, background: 'var(--cream-dark)',
                  borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: b.color }} />
                </div>
                <div style={{ width: 54, fontSize: 12.5, textAlign: 'right',
                  color: 'var(--text-muted)' }}>{n} · {pct}%</div>
              </div>
              {n > 0 && (
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginLeft: 72,
                  marginTop: 2 }}>{b.means}</div>
              )}
            </div>
          )
        })}

        {worst?.n > 0 && (
          <div style={{ fontSize: 12.5, marginTop: 12, padding: '9px 11px', borderRadius: 8,
            background: 'var(--cream)', color: 'var(--text-secondary)' }}>
            Most losses land in <strong>{worst.label}</strong>. {worst.means}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Notes panel ─────────────────────────────────────────────────────────────

function NotesPanel({ profileId, month, notes, profileById, currentUser, onChanged }) {
  const toast = useToast()
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  const monthNotes = notes
    .filter(n => n.profile_id === profileId && n.month === month)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

  const add = async () => {
    if (!draft.trim()) return
    setSaving(true)
    const { error } = await supabase.from('ad_monthly_notes').insert({
      profile_id: profileId,
      month,
      body: draft.trim(),
      author_id: currentUser?.id || null,
    })
    setSaving(false)
    if (error) { toast('Could not save note: ' + error.message, 'error'); return }
    setDraft('')
    toast('Note saved', 'success')
    onChanged()
  }

  const remove = async (id) => {
    const { error } = await supabase.from('ad_monthly_notes').delete().eq('id', id)
    if (error) { toast('Could not delete: ' + error.message, 'error'); return }
    onChanged()
  }

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-header">
        <span className="card-title">1:1 Notes — {monthLabel(month)}</span>
      </div>
      <div className="card-body">
        <textarea
          className="form-control"
          rows={3}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Problem clients, relationship risks, what we're doing about it..."
          style={{ fontSize: 12.5, resize: 'vertical' }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn btn-gold btn-sm" onClick={add} disabled={saving || !draft.trim()}>
            <Plus size={13} /> {saving ? 'Saving...' : 'Add note'}
          </button>
        </div>

        {monthNotes.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 12 }}>
            No notes for this month yet.
          </div>
        ) : (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {monthNotes.map(n => (
              <div key={n.id} style={{
                background: 'var(--cream)', border: '1px solid var(--border-light)',
                borderRadius: 8, padding: '10px 12px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                    {profileById[n.author_id]?.full_name || 'Unknown'} · {new Date(n.created_at).toLocaleDateString()}
                  </div>
                  <button className="btn-icon" onClick={() => remove(n.id)} title="Delete note">
                    <Trash2 size={13} />
                  </button>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'pre-wrap', marginTop: 4 }}>
                  {n.body}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Watchlist ───────────────────────────────────────────────────────────────
// Comes out of the monthly check-in: Tori and the AD agree a client needs
// extra attention, and it stays visible until they're genuinely back on track.
function WatchlistPanel({ profileId, isCompany, entries, items, clients, profileById,
                          currentUser, onAdd, onUpdate, onAddItem, onToggleItem, first90Ids }) {
  const [adding, setAdding] = useState(false)
  const [d, setD] = useState({ client_id: '', reasons: [], severity: 'watch',
    reason_note: '', what_good_looks_like: '', review_by: '' })

  const toggleReason = (key) => setD(prev => ({
    ...prev,
    reasons: prev.reasons.includes(key)
      ? prev.reasons.filter(r => r !== key)
      : [...prev.reasons, key],
  }))
  const [openId, setOpenId] = useState(null)
  const [itemDraft, setItemDraft] = useState({ kind: 'todo', body: '', due_date: '' })

  const open = entries.filter(e => e.status === 'open' || e.status === 'improving')
  const closed = entries.filter(e => e.status === 'resolved' || e.status === 'lost')
  const eligible = clients.filter(c => !c.archived_at &&
    (isCompany || c.account_director_id === profileId) &&
    !open.some(e => e.client_id === c.id))

  const clientName = id => clients.find(c => c.id === id)?.name || 'Unknown'

  return (
    <div className="card" style={{ marginTop: 14, border: open.length ? '1.5px solid var(--mauve)' : undefined }}>
      <div className="card-header" style={{ background: open.length ? 'var(--blush)' : undefined }}>
        <span className="card-title" style={{ color: open.length ? 'var(--mauve)' : undefined }}>
          <Eye size={13} /> Watchlist
          {open.length > 0 && <span style={{ fontWeight: 400 }}> · {open.length}</span>}
        </span>
        <button className="btn btn-ghost btn-sm" onClick={() => setAdding(a => !a)}>
          <Plus size={12} /> Add client
        </button>
      </div>
      <div className="card-body">
        {adding && (
          <div style={{ border: '1px dashed var(--border)', borderRadius: 9, padding: 13, marginBottom: 12 }}>
            <div className="form-group">
              <label className="form-label">Which client?</label>
              <select className="form-control" value={d.client_id}
                onChange={e => setD({ ...d, client_id: e.target.value })}>
                <option value="">— Select —</option>
                {eligible.sort((a, b) => a.name.localeCompare(b.name))
                  .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Why are we watching them?</label>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 5 }}>
                Pick as many as apply. The combination usually matters more than any one.
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {WATCH_REASONS.map(r => {
                  const on = d.reasons.includes(r.key)
                  return (
                    <button key={r.key} onClick={() => toggleReason(r.key)} title={r.hint}
                      style={{ fontSize: 12, padding: '4px 10px', borderRadius: 50, cursor: 'pointer',
                        border: `1px solid ${on ? 'var(--mauve)' : 'var(--border)'}`,
                        background: on ? 'var(--mauve)' : '#fff',
                        color: on ? '#fff' : 'var(--text-secondary)',
                        fontWeight: on ? 600 : 400 }}>
                      {r.label}
                    </button>
                  )
                })}
              </div>
              {d.reasons.length > 0 && (
                <div style={{ marginTop: 7 }}>
                  {d.reasons.map(k => getWatchReason(k)).filter(r => r?.focus).map(r => (
                    <div key={r.key} style={{ display: 'flex', gap: 8, marginBottom: 3 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--mauve)',
                        width: 130, flexShrink: 0 }}>{r.label}</span>
                      <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{r.focus}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">What specifically?</label>
              <textarea className="form-control" rows={2} style={{ fontSize: 12.5, resize: 'vertical' }}
                value={d.reason_note} onChange={e => setD({ ...d, reason_note: e.target.value })}
                placeholder="What came up in the check-in. Their words if you have them." />
            </div>

            <div className="form-group">
              <label className="form-label">What does "back on track" look like?</label>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                Without this nobody can say when this is over, and they stay on the list forever.
              </div>
              <input className="form-control" value={d.what_good_looks_like}
                onChange={e => setD({ ...d, what_good_looks_like: e.target.value })}
                placeholder="Two months of leads at target, and they stop asking about cost." />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">How serious?</label>
                <select className="form-control" value={d.severity}
                  onChange={e => setD({ ...d, severity: e.target.value })}>
                  {Object.entries(WATCH_SEVERITY).map(([k, v]) =>
                    <option key={k} value={k}>{v.label} — {v.note}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Look again by</label>
                <input className="form-control" type="date" value={d.review_by}
                  onChange={e => setD({ ...d, review_by: e.target.value })} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>Cancel</button>
              <button className="btn btn-gold btn-sm"
                disabled={!d.client_id || d.reasons.length === 0}
                onClick={async () => {
                  const ok = await onAdd({
                    ...d,
                    // reason keeps the first one so anything reading a single
                    // value still works; reasons holds the full set.
                    reason: d.reasons[0],
                    reasons: d.reasons,
                    review_by: d.review_by || null,
                  })
                  if (ok !== false) {
                    setD({ client_id: '', reasons: [], severity: 'watch',
                      reason_note: '', what_good_looks_like: '', review_by: '' })
                    setAdding(false)
                  }
                }}>Add to watchlist</button>
            </div>
          </div>
        )}

        {open.length === 0 && !adding && (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Nobody on the watchlist. Add clients here during the monthly check-in.
          </div>
        )}

        {open.map(e => {
          // Older entries only have the single reason column.
          const keys = (e.reasons && e.reasons.length) ? e.reasons : (e.reason ? [e.reason] : [])
          const all = keys.map(getWatchReason).filter(Boolean)
          const r = all[0]
          const sev = WATCH_SEVERITY[e.severity]
          const st = WATCH_STATUS[e.status]
          const mine = items.filter(i => i.watchlist_id === e.id)
          const todos = mine.filter(i => i.kind === 'todo')
          const openTodos = todos.filter(i => !i.done)
          const isOpen = openId === e.id
          const overdue = e.review_by && new Date(e.review_by + 'T12:00:00') < new Date()
          const alsoNew = first90Ids.has(e.client_id)

          return (
            <div key={e.id} style={{ border: `1px solid ${sev.color}`, borderRadius: 10,
              marginBottom: 9, overflow: 'hidden' }}>
              <div onClick={() => setOpenId(isOpen ? null : e.id)}
                style={{ padding: '11px 13px', cursor: 'pointer', background: sev.bg }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--navy)' }}>
                    {clientName(e.client_id)}
                  </span>
                  {all.map(x => (
                    <span key={x.key} style={{ fontSize: 11, fontWeight: 600, color: sev.color,
                      background: '#fff', border: `1px solid ${sev.color}`, borderRadius: 50,
                      padding: '1px 8px' }}>{x.label}</span>
                  ))}
                  {alsoNew && (
                    <span title="Also in their first 90 days — the two views share this client"
                      style={{ fontSize: 9.5, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                        background: 'var(--gold)', color: '#fff' }}>🌱 IN FIRST 90</span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 11.5, color: st.color, fontWeight: 600 }}>
                    {st.label}
                  </span>
                </div>
                <div style={{ fontSize: 11.5, color: overdue ? 'var(--red)' : 'var(--text-muted)', marginTop: 3 }}>
                  since {new Date(e.opened_on + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {e.review_by && ` · review by ${new Date(e.review_by + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                  {overdue && ' · review overdue'}
                  {openTodos.length > 0 && ` · ${openTodos.length} open to-do${openTodos.length === 1 ? '' : 's'}`}
                </div>
              </div>

              {isOpen && (
                <div style={{ padding: '12px 13px', borderTop: `1px solid ${sev.color}` }}>
                  {e.reason_note && (
                    <div style={{ fontSize: 12.5, marginBottom: 9 }}>{e.reason_note}</div>
                  )}
                  {all.filter(x => x.focus).length > 0 && (
                    <div style={{ padding: '8px 10px', borderRadius: 8,
                      background: 'var(--cream)', marginBottom: 9 }}>
                      {all.filter(x => x.focus).map(x => (
                        <div key={x.key} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--mauve)',
                            width: 132, flexShrink: 0 }}>{x.label}</span>
                          <span style={{ fontSize: 12.5 }}>{x.focus}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {e.what_good_looks_like && (
                    <div style={{ fontSize: 12.5, marginBottom: 9 }}>
                      <span style={{ color: 'var(--text-muted)' }}>Back on track means: </span>
                      {e.what_good_looks_like}
                    </div>
                  )}

                  {alsoNew && (
                    <div style={{ fontSize: 12.5, padding: '8px 10px', borderRadius: 8,
                      background: '#fdf6e3', color: '#8a6000', marginBottom: 9 }}>
                      This client is also in their first 90 days. Their touchpoints and goals live
                      in First 90 Days — track the work there, and use this entry for the
                      check-in concern only. Don't run two plans.
                    </div>
                  )}

                  {/* Notes and to-dos */}
                  {mine.map(i => (
                    <div key={i.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start',
                      padding: '6px 0', borderTop: '1px solid var(--border-light)' }}>
                      {i.kind === 'todo' ? (
                        <input type="checkbox" checked={i.done} style={{ marginTop: 3 }}
                          onChange={() => onToggleItem(i)} />
                      ) : (
                        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
                          width: 34, paddingTop: 2 }}>NOTE</span>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12.5,
                          textDecoration: i.done ? 'line-through' : 'none',
                          color: i.done ? 'var(--text-muted)' : 'var(--text-primary)' }}>{i.body}</div>
                        <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                          {profileById[i.author_id]?.full_name || 'Unknown'}
                          {i.due_date && ` · due ${new Date(i.due_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Add note or to-do */}
                  <div style={{ display: 'flex', gap: 7, alignItems: 'flex-end', marginTop: 10, flexWrap: 'wrap' }}>
                    <select className="form-control" style={{ width: 92, fontSize: 12.5 }}
                      value={itemDraft.kind} onChange={ev => setItemDraft({ ...itemDraft, kind: ev.target.value })}>
                      <option value="todo">To-do</option>
                      <option value="note">Note</option>
                      <option value="goal">Goal</option>
                    </select>
                    <input className="form-control" style={{ flex: 1, minWidth: 150, fontSize: 12.5 }}
                      value={itemDraft.body} onChange={ev => setItemDraft({ ...itemDraft, body: ev.target.value })}
                      placeholder={itemDraft.kind === 'note' ? 'What happened' : 'What needs doing'} />
                    {itemDraft.kind !== 'note' && (
                      <input className="form-control" type="date" style={{ width: 140, fontSize: 12.5 }}
                        value={itemDraft.due_date}
                        onChange={ev => setItemDraft({ ...itemDraft, due_date: ev.target.value })} />
                    )}
                    <button className="btn btn-ghost btn-sm" disabled={!itemDraft.body.trim()}
                      onClick={() => {
                        onAddItem(e.id, { kind: itemDraft.kind, body: itemDraft.body.trim(),
                          due_date: itemDraft.due_date || null })
                        setItemDraft({ kind: 'todo', body: '', due_date: '' })
                      }}>Add</button>
                  </div>

                  {/* Status changes */}
                  <div style={{ display: 'flex', gap: 7, marginTop: 12, flexWrap: 'wrap',
                    paddingTop: 10, borderTop: '1px solid var(--border-light)' }}>
                    {e.status === 'open' && (
                      <button className="btn btn-ghost btn-sm"
                        onClick={() => onUpdate(e.id, { status: 'improving' })}>
                        <ArrowRight size={12} /> Improving
                      </button>
                    )}
                    <button className="btn btn-ghost btn-sm"
                      disabled={openTodos.length > 0}
                      title={openTodos.length > 0 ? `${openTodos.length} to-do(s) still open` : 'Graduate off the watchlist'}
                      onClick={() => onUpdate(e.id, { status: 'resolved',
                        closed_on: new Date().toISOString().slice(0, 10) })}>
                      <Check size={12} /> Back on track
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                      onClick={() => onUpdate(e.id, { status: 'lost',
                        closed_on: new Date().toISOString().slice(0, 10) })}>
                      <X size={12} /> They left
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {closed.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border-light)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 0.4, color: 'var(--text-muted)', marginBottom: 6 }}>
              Closed · {closed.length}
            </div>
            {closed.slice(0, 8).map(e => (
              <div key={e.id} style={{ fontSize: 12.5, color: 'var(--text-muted)', padding: '3px 0' }}>
                {clientName(e.client_id)} — {
                  ((e.reasons && e.reasons.length) ? e.reasons : [e.reason])
                    .map(k => getWatchReason(k)?.label).filter(Boolean).join(', ')
                }
                <span style={{ color: WATCH_STATUS[e.status].color, fontWeight: 600 }}>
                  {' · '}{WATCH_STATUS[e.status].label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main view ───────────────────────────────────────────────────────────────

export default function ADScorecardView({ profiles, allClients = [], currentUser, profile }) {
  const toast = useToast()
  const [snapshots, setSnapshots] = useState([])
  const [notes, setNotes] = useState([])
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState(COMPANY_ID)
  const [selectedMonth, setSelectedMonth] = useState(currentMonthKey())
  const [refreshing, setRefreshing] = useState(false)
  const [watch, setWatch] = useState([])
  const [watchItems, setWatchItems] = useState([])
  const [first90Ids, setFirst90Ids] = useState(new Set())

  const profileById = useMemo(() => {
    const map = {}
    profiles.forEach(p => { map[p.id] = p })
    return map
  }, [profiles])

  const fullAccess = canViewAllScorecards(currentUser?.id)

  // An AD sees their own card and the company view. Only Lauren and Tori see
  // everyone — cross-comparing ADs is Tori's conversation to have, not
  // something to run into by accident.
  const directors = useMemo(
    () => visibleDirectorsFor(currentUser?.id, profile,
      resolveDirectors(profiles, allClients)),
    [profiles, allClients, currentUser, profile]
  )

  const load = useCallback(async () => {
    setLoading(true)
    const [snapRes, noteRes, histRes, wRes, wiRes, p90Res] = await Promise.all([
      supabase.from('ad_monthly_snapshots').select('*').order('month', { ascending: true }),
      supabase.from('ad_monthly_notes').select('*'),
      supabase.from('ad_company_history').select('*').order('month', { ascending: true }),
      supabase.from('watchlist').select('*').order('opened_on', { ascending: false }),
      supabase.from('watchlist_items').select('*').order('created_at'),
      // Clients currently in their first 90 days — used only to cross-link the
      // two sections so the same client isn't worked twice.
      supabase.from('client_90day').select('client_id, phase'),
    ])
    if (snapRes.error) toast('Could not load snapshots: ' + snapRes.error.message, 'error')
    if (noteRes.error) toast('Could not load notes: ' + noteRes.error.message, 'error')
    setSnapshots((snapRes.data || []).filter(r => !AD_SCORECARD_EXCLUDED.includes(r.profile_id)))
    setNotes(noteRes.data || [])
    // Missing table is fine — the company view just falls back to the roll-up.
    setHistory(histRes.error ? [] : (histRes.data || []))
    setWatch(wRes.error ? [] : (wRes.data || []))
    setWatchItems(wiRes.error ? [] : (wiRes.data || []))
    setFirst90Ids(new Set((p90Res.error ? [] : (p90Res.data || []))
      .filter(r => r.phase === 'onboarding').map(r => r.client_id)))
    setLoading(false)
  }, [toast])

  useEffect(() => { load() }, [load])



  // Rows for the selected AD, oldest -> newest (turnover needs the prior month).
  const companyRows = useMemo(() => buildCompanyRows(snapshots, history), [snapshots, history])
  const isCompany = selectedId === COMPANY_ID

  const rows = useMemo(
    () => isCompany ? companyRows : snapshots.filter(s => s.profile_id === selectedId),
    [snapshots, companyRows, isCompany, selectedId]
  )

  const latest = rows.length ? rows[rows.length - 1] : null
  const prior = rows.length > 1 ? rows[rows.length - 2] : null
  const latestTurnover = turnoverRate(latest, prior)
  const latestRolling = rollingTurnover(rows, rows.length - 1)

  // Average the recent monthly rates, then annualize — one month is too noisy
  // to project a year from.
  const avgMonthly = useMemo(() => {
    const rates = rows
      .map((r, i) => turnoverRate(r, i > 0 ? rows[i - 1] : null))
      .filter(v => v !== null)
      .slice(-12)
    if (!rates.length) return null
    return rates.reduce((a, b) => a + b, 0) / rates.length
  }, [rows])
  const annual = annualize(avgMonthly)

  // Average client count over the trailing window — the base the annual
  // targets are expressed against.
  const avgBase = useMemo(() => {
    const w = rows.slice(-12).map(r => r.total_clients || 0).filter(Boolean)
    return w.length ? w.reduce((a, b) => a + b, 0) / w.length : 0
  }, [rows])

  // tenureMonths is null when turnover is 0% (nothing to divide by), so this
  // has to be guarded separately from `annual` itself.
  const annualSub = annual?.tenureMonths
    ? `~${annual.tenureMonths.toFixed(0)} mo avg tenure`
    : annual ? 'no churn on record' : 'needs history'

  // Recompute the open month on demand — useful right after reassigning clients.
  const recompute = async () => {
    setRefreshing(true)
    const { error } = await supabase.rpc('build_ad_month_snapshot', {
      target_month: currentMonthKey(),
    })
    setRefreshing(false)
    if (error) { toast('Recompute failed: ' + error.message, 'error'); return }
    toast('Current month recalculated', 'success')
    load()
  }

  const saveOverride = async (row, value) => {
    const { error } = await supabase
      .from('ad_monthly_snapshots')
      .update({ terminating_override: value })
      .eq('id', row.id)
    if (error) { toast('Could not save: ' + error.message, 'error'); return }
    setSnapshots(prev => prev.map(s => s.id === row.id ? { ...s, terminating_override: value } : s))
  }

  const toggleLock = async (row) => {
    const { error } = await supabase
      .from('ad_monthly_snapshots')
      .update({ locked: !row.locked })
      .eq('id', row.id)
    if (error) { toast('Could not update: ' + error.message, 'error'); return }
    setSnapshots(prev => prev.map(s => s.id === row.id ? { ...s, locked: !row.locked } : s))
  }

  const addWatch = async (fields) => {
    const { error } = await supabase.from('watchlist').insert({
      ...fields,
      profile_id: isCompany ? (allClients.find(c => c.id === fields.client_id)?.account_director_id || null) : selectedId,
      opened_by: currentUser?.id || null,
    })
    if (error) { toast('Could not add: ' + error.message, 'error'); return false }
    toast('Added to watchlist', 'success'); await load(); return true
  }
  const updateWatch = async (id, fields) => {
    const { error } = await supabase.from('watchlist').update(fields).eq('id', id)
    if (error) { toast('Could not update: ' + error.message, 'error'); return }
    if (fields.status === 'resolved') toast('Back on track 🎉', 'success')
    load()
  }
  const addWatchItem = async (watchlistId, fields) => {
    const { error } = await supabase.from('watchlist_items').insert({
      watchlist_id: watchlistId, author_id: currentUser?.id || null, ...fields })
    if (error) { toast('Could not add: ' + error.message, 'error'); return }
    load()
  }
  const toggleWatchItem = async (item) => {
    const { error } = await supabase.from('watchlist_items')
      .update({ done: !item.done, done_on: item.done ? null : new Date().toISOString().slice(0, 10) })
      .eq('id', item.id)
    if (error) { toast('Could not update: ' + error.message, 'error'); return }
    load()
  }

  if (loading) {
    return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading scorecards...</div>
  }

  const selected = profileById[selectedId]

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--navy)' }}>
          📈 AD Scorecards
          <span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 400 }}>
            {fullAccess
              ? ` · ${directors.length} directors`
              : ' · your card and the company view'}
          </span>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={recompute} disabled={refreshing}>
          <RefreshCw size={13} /> {refreshing ? 'Recalculating...' : 'Recalculate this month'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* AD list */}
        <div className="card" style={{ width: 230, flexShrink: 0 }}>
          <div className="card-header"><span className="card-title">Account Directors</span></div>
          <div style={{ padding: 8 }}>
            {(() => {
              const isActive = selectedId === COMPANY_ID
              const latestCo = companyRows.length ? companyRows[companyRows.length - 1] : null
              return (
                <div onClick={() => setSelectedId(COMPANY_ID)}
                  style={{
                    padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 8,
                    background: isActive ? 'var(--gold)' : 'var(--cream-dark)',
                    borderBottom: '1px solid var(--border-light)',
                  }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Company — all ADs</div>
                  <div style={{ fontSize: 11.5, opacity: 0.7 }}>
                    {latestCo ? `${latestCo.total_clients} clients` : 'No data yet'}
                  </div>
                </div>
              )
            })()}
            {directors.length === 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic', padding: 10 }}>
                No profiles with the AD role yet.
              </div>
            )}
            {directors.map(p => {
              const theirRows = snapshots.filter(s => s.profile_id === p.id)
              const theirLatest = theirRows.length ? theirRows[theirRows.length - 1] : null
              const isActive = p.id === selectedId
              return (
                <div key={p.id} onClick={() => setSelectedId(p.id)}
                  style={{
                    padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                    background: isActive ? 'var(--navy)' : 'transparent',
                    color: isActive ? '#fff' : 'var(--text-primary)',
                  }}>
                  <div style={{ fontSize: 13, fontWeight: isActive ? 600 : 500 }}>{p.full_name || 'Unnamed'}</div>
                  <div style={{ fontSize: 11.5, opacity: isActive ? 0.8 : 0.6 }}>
                    {theirLatest ? `${theirLatest.total_clients} clients` : 'No data yet'}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Selected AD */}
        <div style={{ flex: 1, minWidth: 480 }}>
          {!selected && !isCompany ? (
            <div className="empty-state">Select an Account Director.</div>
          ) : (
            <>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 19, color: 'var(--navy)', marginBottom: 10 }}>
                {isCompany ? 'Company — all ADs' : (selected?.full_name || 'Unnamed')}
                {!isCompany && selected?.role === 'supervising_ad' && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 }}> · Supervising AD</span>
                )}
              </div>

              {/* Current-month stats */}
              {latest ? (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                  <StatCard label="AD Clients" value={latest.total_clients} sub={monthLabel(latest.month)} />
                  <StatCard label="AM Clients" value={latest.am_clients} sub="also managing" />
                  <StatCard label="New" value={latest.new_clients} sub="this month" />
                  <StatCard label="Terminated" value={effectiveTerminating(latest)} sub="this month" />
                  <StatCard label="Turnover" value={formatPct(latestTurnover)} sub="this month" tone={turnoverTone(latestTurnover)} />
                  <StatCard label="Rolling 12mo" value={formatPct(latestRolling)} sub="trailing window" tone={annualTone(latestRolling)} />
                  <StatCard
                    label="Annualized"
                    value={annual ? formatPct(annual.attrition, 0) : '—'}
                    sub={annualSub}
                    tone={annualTone(annual?.attrition)} />
                </div>
              ) : (
                <div className="empty-state" style={{ marginBottom: 14 }}>
                  <Users size={22} className="empty-state-icon" />
                  <div>No months on record yet. Run the backfill in ad_scorecard.sql, or hit Recalculate.</div>
                </div>
              )}

              {/* Target vs the 3-5%/mo goal, expressed in clients */}
              {latest && prior && (
                <TargetBar
                  base={prior.total_clients}
                  actual={effectiveTerminating(latest)}
                  rollingPct={latestRolling}
                  annualPct={annual?.attrition ?? null}
                  avgBase={avgBase}
                />
              )}

              {/* History table — newest first for reading */}
              <div className="card" style={{ overflow: 'auto' }}>
                <div className="card-header"><span className="card-title">Monthly history</span></div>
                <table className="task-table" style={{ width: '100%', minWidth: 720, fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th style={{ textAlign: 'right' }}>Total Clients</th>
                      <th style={{ textAlign: 'right' }}>AM Clients</th>
                      <th style={{ textAlign: 'right' }}>New</th>
                      <th style={{ textAlign: 'right' }}>Terminating</th>
                      <th style={{ textAlign: 'right' }}>Net</th>
                      <th style={{ textAlign: 'right' }}>Turnover</th>
                      <th style={{ textAlign: 'right' }}>Notes</th>
                      <th style={{ width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={9} style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        No history yet
                      </td></tr>
                    ) : rows.map((row, i) => {
                      const pct = turnoverRate(row, i > 0 ? rows[i - 1] : null)
                      const tone = turnoverTone(pct)
                      const net = netGrowth(row)
                      const noteCount = notes.filter(n => n.profile_id === row.profile_id && n.month === row.month).length
                      const isSelectedMonth = row.month === selectedMonth
                      return (
                        <tr key={row.id}
                          onClick={() => setSelectedMonth(row.month)}
                          style={{ cursor: 'pointer', background: isSelectedMonth ? 'var(--cream-dark)' : undefined }}>
                          <td style={{ fontWeight: 500 }}>
                            {monthLabel(row.month)}
                            {row.from_history && (
                              <span title="Company total from historical records, before per-AD tracking began"
                                style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 5 }}>hist</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>{row.total_clients}</td>
                          <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{row.am_clients || '—'}</td>
                          <td style={{ textAlign: 'right', color: 'var(--green)' }}>{row.new_clients || '—'}</td>
                          <td style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                            <TerminatingCell row={row} onSave={saveOverride} disabled={row.locked || isCompany} />
                          </td>
                          <td style={{ textAlign: 'right', color: net > 0 ? 'var(--green)' : net < 0 ? 'var(--red)' : 'var(--text-muted)' }}>
                            {net > 0 ? <TrendingUp size={11} /> : net < 0 ? <TrendingDown size={11} /> : null} {net > 0 ? `+${net}` : net}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <span style={{ color: tone.color, fontWeight: 600 }}>{formatPct(pct)}</span>
                          </td>
                          <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{noteCount || '—'}</td>
                          <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                            {!isCompany && <button className="btn-icon" onClick={() => toggleLock(row)}
                              title={row.locked ? 'Locked — click to reopen' : 'Open — click to lock'}>
                              {row.locked ? <Lock size={13} /> : <Unlock size={13} />}
                            </button>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                <div style={{ padding: '8px 14px', fontSize: 11.5, color: 'var(--text-muted)', borderTop: '1px solid var(--border-light)' }}>
                  Turnover = terminations ÷ previous month's client count. First month on record shows a dash.
                  Rows marked <strong>hist</strong> are company totals from records predating per-AD tracking.
                  A <strong>*</strong> means the terminating count was entered by hand.
                </div>
              </div>

              <ChurnByCause rows={rows} />

              <ChurnSpeed rows={rows} />

              <WatchlistPanel
                profileId={selectedId}
                isCompany={isCompany}
                entries={isCompany && fullAccess ? watch : watch.filter(w =>
                  w.profile_id === selectedId ||
                  allClients.find(c => c.id === w.client_id)?.account_director_id === selectedId)}
                items={watchItems}
                clients={allClients}
                profileById={profileById}
                currentUser={currentUser}
                first90Ids={first90Ids}
                onAdd={addWatch}
                onUpdate={updateWatch}
                onAddItem={addWatchItem}
                onToggleItem={toggleWatchItem}
              />

              {!isCompany && <NotesPanel
                profileId={selectedId}
                month={selectedMonth}
                notes={notes}
                profileById={profileById}
                currentUser={currentUser}
                onChanged={load}
              />}
            </>
          )}
        </div>
      </div>
    </div>
  )
}